import { useEffect, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import useUserStore from "@store/useUserStore";
import { useSnackbar } from "contexts/SnackbarContext";
import SubHeader from "components/common/SubHeader";
import ProfileAvatar from "components/common/ProfileAvatar";
import LoadingSpinner from "components/common/LoadingSpiner";
import PbRecordGrid from "components/profile/PbRecordGrid";
import MediaViewer from "components/common/MediaViewer";
import { getInstructorDetail } from "api/instructor";
import { getProfileV2 } from "api/user";
import { startDirectMessageRoom } from "api/chat";
import { MESSAGING_ENABLED } from "constants/featureFlags";
import { matchesUserParam } from "utils/userIdentity";
import "assets/scss/pages/instructorProfile.scss";

const InstructorProfile = () => {
    const navigate = useNavigate();
    const { userId } = useParams();
    const userInfo = useUserStore((state) => state.userInfo);
    const { showError } = useSnackbar();
    const [profile, setProfile] = useState(null);
    const [pbRecords, setPbRecords] = useState([]);
    const [isLoading, setIsLoading] = useState(true);
    const [notFound, setNotFound] = useState(false);
    const [viewerMedia, setViewerMedia] = useState(null);
    const [viewerIndex, setViewerIndex] = useState(0);
    const [slideIndex, setSlideIndex] = useState(0);
    const slideTrackRef = useRef(null);

    // 신규 5개 필드(main_slides 등)는 공개 강사 상세(v1)가 내려준다 (BE #400 반영).
    // TODO(BE #400): best_records까지 강사 상세에 포함되면 getProfileV2 호출을 제거하고
    // getInstructorDetail 1회 호출로 단순화한다.
    useEffect(() => {
        if (!userId) return;
        setIsLoading(true);
        setNotFound(false);
        Promise.all([
            getInstructorDetail(userId),
            // v2 통합 프로필은 PB(best_records) 전용 — 인증 필수라
            // 비로그인 401은 전역 인터셉터가 로그인 페이지로 보낸다
            getProfileV2(userId),
        ]).then(([detail, v2]) => {
            if (!detail) {
                setNotFound(true);
            } else {
                setProfile(detail);
            }
            // v2 조회 실패 시 PB 섹션 없이 렌더링
            setPbRecords(v2?.best_records ?? []);
            setIsLoading(false);
        }).catch(() => {
            setNotFound(true);
            setIsLoading(false);
        });
    }, [userId]);

    if (isLoading) return <LoadingSpinner />;

    if (notFound || !profile) {
        return (
            <div className="instructor-profile-page">
                <SubHeader title="강사 프로필" />
                <div className="instructor-profile-empty">
                    <p>프로필을 찾을 수 없습니다.</p>
                </div>
            </div>
        );
    }

    const displayName = profile.name || "강사";
    const certificates = profile.certificates ?? [];
    const instructorImage = profile.profile_image_url || null;
    const sports = profile.sports ?? [];
    const interests = profile.interests || "";
    const isOwnProfile = matchesUserParam(userInfo, userId);

    // BE #400 신규 필드 — 공개 강사 상세(v1) 응답 (미입력 시 null/[] — 섹션 미노출)
    const mainSlides = profile.main_slides ?? [];
    const freeGalleries = profile.free_galleries ?? [];
    const careerSummary = profile.career_summary || "";
    const certificateGroupLabels = profile.certificate_group_labels ?? [];
    const detailedCareers = profile.detailed_careers ?? [];
    // 2장 이상이면 캐러셀, 1장이면 단일 히어로, 0장이면 강습용 사진 fallback
    const hasMainSlides = mainSlides.length >= 2;
    const heroImage = mainSlides[0] || instructorImage;
    const hasCareerSummary = certificateGroupLabels.length > 0 || !!careerSummary;

    const openViewer = (urls, index) => {
        setViewerMedia(urls.map((url) => ({ url, type: "image" })));
        setViewerIndex(index);
    };

    const handleSlideScroll = () => {
        const track = slideTrackRef.current;
        if (!track || !track.clientWidth) return;
        setSlideIndex(Math.round(track.scrollLeft / track.clientWidth));
    };

    const handleStartMessage = async () => {
        if (!userInfo) {
            navigate("/login");
            return;
        }
        try {
            const room = await startDirectMessageRoom(userId);
            if (room?.id) {
                navigate(`/messages/${encodeURIComponent(room.id)}`);
                return;
            }
            showError("메시지방을 시작하지 못했습니다.");
        } catch {
            showError("메시지방을 시작하지 못했습니다.");
        }
    };

    return (
        <div className="instructor-profile-page">
            <SubHeader title="강사 프로필" />

            {hasMainSlides ? (
                <div className="instructor-profile-hero instructor-profile-hero--carousel">
                    <div
                        className="instructor-profile-hero__track"
                        ref={slideTrackRef}
                        onScroll={handleSlideScroll}
                    >
                        {mainSlides.map((url, index) => (
                            <img
                                key={url}
                                src={url}
                                alt={`${displayName} 메인 사진 ${index + 1}`}
                                className="instructor-profile-hero__image"
                                onClick={() => openViewer(mainSlides, index)}
                                role="button"
                            />
                        ))}
                    </div>
                    <span className="instructor-profile-hero__counter">
                        {Math.min(slideIndex + 1, mainSlides.length)}/{mainSlides.length}
                    </span>
                    <div className="instructor-profile-hero__dots">
                        {mainSlides.map((url, index) => (
                            <span
                                key={url}
                                className={`instructor-profile-hero__dot${
                                    index === slideIndex ? " active" : ""
                                }`}
                            />
                        ))}
                    </div>
                </div>
            ) : (
                <div className="instructor-profile-hero">
                    {heroImage ? (
                        <img
                            src={heroImage}
                            alt=""
                            className="instructor-profile-hero__image"
                            onClick={() => openViewer([heroImage], 0)}
                            role="button"
                        />
                    ) : (
                        <div className="instructor-profile-hero__placeholder">
                            <ProfileAvatar imageUrl={null} size={120} />
                            <p className="instructor-profile-hero__placeholder-text">
                                등록된 강사 사진이 없습니다
                            </p>
                        </div>
                    )}
                </div>
            )}

            <div className="instructor-profile-identity">
                {/* 히어로가 대표 이미지(메인 슬라이드)일 때만 강습용 프로필 사진을 원형으로 노출
                    (슬라이드 0장이면 히어로가 이미 강습용 사진이라 중복 노출 방지) */}
                {mainSlides.length > 0 && instructorImage && (
                    <div className="instructor-profile-identity__avatar">
                        <ProfileAvatar imageUrl={instructorImage} size={88} />
                    </div>
                )}
                <div className="instructor-profile-identity__name-row">
                    <span className="instructor-profile-identity__name">
                        {displayName}
                    </span>
                    {sports.map((s) => (
                        <span
                            key={s.id}
                            className="instructor-profile-summary__chip instructor-profile-summary__chip--purple"
                        >
                            {s.name}
                        </span>
                    ))}
                    {profile.brand_name && (
                        <span className="instructor-profile-identity__brand">
                            {profile.brand_name}
                        </span>
                    )}
                </div>
                {profile.introduction && (
                    <p className="instructor-profile-identity__bio">
                        {profile.introduction}
                    </p>
                )}
            </div>

            {hasCareerSummary && (
                <div className="instructor-profile-section">
                    <p className="instructor-profile-section__title">주요 경력 요약</p>
                    {certificateGroupLabels.length > 0 && (
                        <div className="instructor-profile-summary__chips">
                            {certificateGroupLabels.map((label) => (
                                <span
                                    key={label}
                                    className="instructor-profile-summary__chip"
                                >
                                    {label}
                                </span>
                            ))}
                        </div>
                    )}
                    {careerSummary && (
                        <p className="instructor-profile-summary__text">
                            {careerSummary}
                        </p>
                    )}
                </div>
            )}

            {detailedCareers.length > 0 && (
                <div className="instructor-profile-section">
                    <p className="instructor-profile-section__title">상세 경력</p>
                    <div className="instructor-profile-career-list">
                        {detailedCareers.map((career, index) => (
                            <div
                                key={`${career.role}-${index}`}
                                className="instructor-profile-career"
                            >
                                <div className="instructor-profile-career__head">
                                    <p className="instructor-profile-career__role">
                                        {career.role}
                                    </p>
                                    <p className="instructor-profile-career__years">
                                        경력 {career.years}년
                                    </p>
                                </div>
                                {(career.bullets ?? []).length > 0 && (
                                    <ul className="instructor-profile-career__bullets">
                                        {career.bullets.map((bullet, bulletIndex) => (
                                            <li key={bulletIndex}>{bullet}</li>
                                        ))}
                                    </ul>
                                )}
                            </div>
                        ))}
                    </div>
                </div>
            )}

            {freeGalleries.length > 0 && (
                <div className="instructor-profile-section instructor-profile-section--flush">
                    <p className="instructor-profile-section__title">활동 사진</p>
                    <div className="instructor-profile-gallery">
                        {freeGalleries.map((url, index) => (
                            <img
                                key={url}
                                src={url}
                                alt={`활동 사진 ${index + 1}`}
                                className="instructor-profile-gallery__item"
                                onClick={() => openViewer(freeGalleries, index)}
                                role="button"
                            />
                        ))}
                    </div>
                </div>
            )}

            {interests && (
                <div className="instructor-profile-section instructor-profile-section--flush">
                    <p className="instructor-profile-section__title">관심사</p>
                    <p className="instructor-profile-meta__text">
                        {interests}
                    </p>
                </div>
            )}

            <div className="instructor-profile-section instructor-profile-section--flush">
                <p className="instructor-profile-section__title">자격증</p>
                {certificates.length === 0 ? (
                    <p className="instructor-profile-section__empty">
                        등록된 자격증이 없습니다
                    </p>
                ) : (
                    <div className="instructor-profile-cert-row__chips">
                        {[...certificates]
                            .sort((a, b) => (b.id ?? 0) - (a.id ?? 0))
                            .map((c) => (
                                <span key={c.id} className="profile-card__cert-chip">
                                    {c.name}
                                </span>
                            ))}
                    </div>
                )}
            </div>

            {pbRecords.length > 0 && (
                <div className="instructor-profile-section instructor-profile-section--pb">
                    <p className="instructor-profile-section__title">PB 기록</p>
                    <PbRecordGrid records={pbRecords} />
                </div>
            )}

            <div className="instructor-profile-cta">
                {MESSAGING_ENABLED && !isOwnProfile && (
                    <button
                        type="button"
                        className="instructor-profile-cta__message"
                        onClick={handleStartMessage}
                    >
                        메시지
                    </button>
                )}
                <button
                    type="button"
                    className="instructor-profile-cta__community"
                    onClick={() => navigate(`/community/user/${encodeURIComponent(userId)}`)}
                >
                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none">
                        <circle cx="12" cy="8" r="4" stroke="currentColor" strokeWidth="1.6" />
                        <path
                            d="M4 21c0-4.4 3.6-8 8-8s8 3.6 8 8"
                            stroke="currentColor"
                            strokeWidth="1.6"
                            strokeLinecap="round"
                        />
                    </svg>
                    커뮤니티 프로필 확인하러 가기
                </button>
            </div>

            {viewerMedia && (
                <MediaViewer
                    media={viewerMedia}
                    initialIndex={viewerIndex}
                    title={displayName}
                    onClose={() => setViewerMedia(null)}
                />
            )}
        </div>
    );
};

export default InstructorProfile;
